import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { Chart, registerables } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

interface SimUsageDeviceRow {
  deviceId: string;
  deviceSn: string;
  simCcid: string;
  /** Used only for product-code filter (column not shown). */
  product: string;
  dataUsedMb: number;
  thresholdMb: number;
  lastSeen: string;
}

interface ProductUsageSlice {
  id: string;
  label: string;
  pct: number;
  count: number;
  color: string;
}

@Component({
  selector: "app-sim-usage-report",
  templateUrl: "./sim-usage-report.component.html",
  styleUrls: ["./sim-usage-report.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SimUsageReportComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly canvasId = "simUsageDonutChart";
  private donutChart: any = null;
  private readonly destroy$ = new Subject<void>();

  readonly fleetControl = new UntypedFormControl("Metro Fleet");
  readonly fleetOptions = [
    "Metro Fleet",
    "Regional Fleet",
    "National Fleet",
  ];

  readonly productCodeControl = new UntypedFormControl("DL972");
  readonly productCodeOptions = ["DL972", "DL974", "DCM970", "Other"];

  readonly totalDevices = 1527;

  readonly productSlices: ProductUsageSlice[] = [
    {
      id: "dl972",
      label: "DL972",
      pct: 55,
      count: 840,
      color: "#10B77F",
    },
    {
      id: "dl974",
      label: "DL974",
      pct: 25,
      count: 382,
      color: "#4E8DDE",
    },
    {
      id: "dcm970",
      label: "DCM970",
      pct: 15,
      count: 229,
      color: "#F59F0A",
    },
    {
      id: "other",
      label: "Other",
      pct: 5,
      count: 76,
      color: "#94A3B8",
    },
  ];

  deviceSearch = "";
  currentPage = 1;
  readonly pageSize = 5;

  private readonly allDevices: SimUsageDeviceRow[] = [
    {
      deviceId: "DL1234",
      deviceSn: "SN-DL-890012345",
      simCcid: "89981234100012345678",
      product: "DL972",
      dataUsedMb: 490,
      thresholdMb: 500,
      lastSeen: "10m ago",
    },
    {
      deviceId: "DL4277",
      deviceSn: "SN-DL-772211004",
      simCcid: "89981234100022334455",
      product: "DL972",
      dataUsedMb: 320,
      thresholdMb: 500,
      lastSeen: "1m ago",
    },
    {
      deviceId: "DL1345",
      deviceSn: "SN-DL-334455667",
      simCcid: "89981234100099887766",
      product: "DL972",
      dataUsedMb: 520,
      thresholdMb: 500,
      lastSeen: "3m ago",
    },
    {
      deviceId: "DL3545",
      deviceSn: "SN-DL-556677889",
      simCcid: "89981234100011223344",
      product: "DL972",
      dataUsedMb: 400,
      thresholdMb: 500,
      lastSeen: "5h ago",
    },
    {
      deviceId: "DL4251",
      deviceSn: "SN-DL-990011223",
      simCcid: "89981234100055667788",
      product: "DL972",
      dataUsedMb: 560,
      thresholdMb: 500,
      lastSeen: "1d ago",
    },
    {
      deviceId: "DL9012",
      deviceSn: "SN-DL-441122334",
      simCcid: "89981234100033445566",
      product: "DL974",
      dataUsedMb: 180,
      thresholdMb: 400,
      lastSeen: "2h ago",
    },
    {
      deviceId: "DL7788",
      deviceSn: "SN-DL-667788990",
      simCcid: "89981234100077889900",
      product: "DL974",
      dataUsedMb: 350,
      thresholdMb: 400,
      lastSeen: "45m ago",
    },
    {
      deviceId: "DL2210",
      deviceSn: "SN-DCM-11002233",
      simCcid: "89981234100044556677",
      product: "DCM970",
      dataUsedMb: 95,
      thresholdMb: 200,
      lastSeen: "1d ago",
    },
    {
      deviceId: "DL6654",
      deviceSn: "SN-DCM-44005566",
      simCcid: "89981234100066778899",
      product: "DCM970",
      dataUsedMb: 210,
      thresholdMb: 200,
      lastSeen: "30m ago",
    },
    {
      deviceId: "DL3300",
      deviceSn: "SN-DL-88009911",
      simCcid: "89981234100000112233",
      product: "DL972",
      dataUsedMb: 275,
      thresholdMb: 500,
      lastSeen: "8h ago",
    },
    {
      deviceId: "DL4411",
      deviceSn: "SN-DL-22334455",
      simCcid: "89981234100044550011",
      product: "DL972",
      dataUsedMb: 500,
      thresholdMb: 500,
      lastSeen: "12m ago",
    },
    {
      deviceId: "DL5522",
      deviceSn: "SN-X-99001122",
      simCcid: "89981234100099001122",
      product: "Other",
      dataUsedMb: 120,
      thresholdMb: 300,
      lastSeen: "3d ago",
    },
  ];

  ngOnInit(): void {
    Chart.register(...registerables);
    this.productCodeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.currentPage = 1;
      });
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => this.renderDonut());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.donutChart) {
      this.donutChart.destroy();
      this.donutChart = null;
    }
  }

  pctUsed(row: SimUsageDeviceRow): number {
    if (!row.thresholdMb) {
      return 0;
    }
    return Math.round((row.dataUsedMb / row.thresholdMb) * 100);
  }

  isExceeded(row: SimUsageDeviceRow): boolean {
    return row.dataUsedMb > row.thresholdMb;
  }

  usagePctClass(row: SimUsageDeviceRow): string {
    if (this.isExceeded(row)) {
      return "sim-usage-pct--exceeded";
    }
    const p = this.pctUsed(row);
    if (p >= 90) {
      return "sim-usage-pct--warn";
    }
    return "sim-usage-pct--ok";
  }

  get filteredDevices(): SimUsageDeviceRow[] {
    const code = String(this.productCodeControl.value ?? "").trim();
    const byProduct = this.allDevices.filter((r) => r.product === code);
    const q = this.deviceSearch.trim().toLowerCase();
    if (!q) {
      return [...byProduct];
    }
    return byProduct.filter(
      (r) =>
        r.deviceId.toLowerCase().includes(q) ||
        r.deviceSn.toLowerCase().includes(q) ||
        r.simCcid.toLowerCase().includes(q) ||
        r.lastSeen.toLowerCase().includes(q)
    );
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.filteredDevices.length / this.pageSize));
  }

  get pagedDevices(): SimUsageDeviceRow[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredDevices.slice(start, start + this.pageSize);
  }

  get globalRowOffset(): number {
    return (this.currentPage - 1) * this.pageSize;
  }

  onSearchInput(): void {
    this.currentPage = 1;
  }

  goPage(page: number): void {
    const p = Math.min(Math.max(1, page), this.pageCount);
    this.currentPage = p;
  }

  goFirst(): void {
    this.goPage(1);
  }

  goPrev(): void {
    this.goPage(this.currentPage - 1);
  }

  goNext(): void {
    this.goPage(this.currentPage + 1);
  }

  goLast(): void {
    this.goPage(this.pageCount);
  }

  onCalendarClick(): void {
    // Mock: wire to date picker when API is available
  }

  onExportClick(): void {
    // Mock
  }

  trackDevice(_i: number, row: SimUsageDeviceRow): string {
    return row.deviceId + row.simCcid + row.lastSeen;
  }

  formatMb(mb: number): string {
    return `${mb}MB`;
  }

  private renderDonut(): void {
    if (this.donutChart) {
      this.donutChart.destroy();
      this.donutChart = null;
    }
    const canvas = document.getElementById(
      this.canvasId
    ) as HTMLCanvasElement | null;
    if (!canvas) {
      return;
    }
    const slices = this.productSlices;
    this.donutChart = new Chart(canvas, {
      type: "doughnut",
      data: {
        labels: slices.map((s) => s.label),
        datasets: [
          {
            data: slices.map((s) => s.count),
            backgroundColor: slices.map((s) => s.color),
            borderColor: "#fff",
            borderWidth: 2,
            hoverOffset: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "68%",
        rotation: -90,
        circumference: 360,
        layout: { padding: 8 },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (item) => {
                const raw = Number(item.raw);
                const pct = this.totalDevices
                  ? Math.round((raw / this.totalDevices) * 100)
                  : 0;
                return `${item.label ?? ""}: ${raw} (${pct}%)`;
              },
            },
          },
        },
      },
    });
  }
}
